<template>
  <div class="run-board">

    <table v-if="!$store.state.isMobileSmall" class="leaderboard-table">
      <thead>
        <tr>
          <th class="ranks">Rank</th>
          <th>Runner</th>
          <th>Time</th>
          <th>Date</th>
          <th>Points</th>
          <th v-if="hasPlatformFilter">Platform</th>
        </tr>
      </thead>
      <tbody v-if="!runs">
        <tr v-for="i in 10" :key="i">
          <td colspan="999">
            <ContentLoader width="170" height="18" primary-color="#1f2a3c" secondary-color="#2c3b55">
                <rect x="0" y="5" rx="2" ry="2" :width="(150 + Math.random() * 20)" height="10" />
            </ContentLoader>
          </td>
        </tr>
      </tbody>
      <tbody v-else-if="runs.length == 0">
        <tr>
          <td colspan="999">
            No runs to show yet!
          </td>
        </tr>
      </tbody>
      <tbody v-else-if="filteredEntries">
        <tr v-if="filteredEntries.length == 0">
          <td colspan="999">
            No runs match the specified filter(s)
          </td>
        </tr>
        <template v-for="entry in filteredEntries" :key="entry.RunId">
          <tr class="run-info">
            <td class="ranks">
                <div v-if="entry.Rank <= 3" class="trophy" v-bind:class="'rank-' + entry.Rank"></div>
                <span v-if="entry.Rank > 3" class="rank">{{ entry.Rank }}</span>
            </td>
            <td>
              <span v-for="(p,i) in entry.Participants" :key="p">
                <router-link :to="'/profiles/' + p.Username">{{ p.Username }}</router-link><span v-if="entry.Participants.length > 0 && i < entry.Participants.length-1">, </span>
              </span>
            </td>
            <td class="timecode">
              <a target="_blank" rel="noopener noreferrer" v-if="entry.Participants[0].EvidenceLink" :href="entry.Participants[0].EvidenceLink" class="timecode">{{ $filters.durationDisplay(entry.Duration) }}</a>
              <span v-else class="timecode">{{$filters.durationDisplay(entry.Duration)}}</span>
              <template v-if="entry.Participants.length > 1">
                <template v-for="partner in entry.Participants.slice(1)" :key="partner.Username">
                  <a target="_blank" rel="noopener noreferrer" v-if="partner.EvidenceLink" :href="partner.EvidenceLink" :title="partner.Username" class="partner-evidence"><i class="fas fa-video"></i></a>
                </template>
              </template>
            </td>
            <td>{{ formatDate(entry.OccurredAt) }}</td>
            <td>{{ entry.Points }}</td>
            <td v-if="hasPlatformFilter">
              {{entry.FilterValues["Platform"]}}
            </td>
          </tr>
          <tr v-if="extraFilters" class="run-filters">
            <td :colspan="hasPlatformFilter ? 6 : 5">
              <template  v-for="(filterValue, filter) in extraFilters" :key="filter">
                <span class="filter-value"  v-if="entry.FilterValues[filter]">
                    {{filter}}: {{entry.FilterValues[filter]}}
                </span>
              </template>
            </td>
          </tr>
        </template>
        
      </tbody>
    </table>
    <div v-else class="run-cards">
      <template v-if="!runs">
        <div class="run-card" v-for="i in 10" :key="i">
          <div class="run-info">
            <ContentLoader class="headline" width="170" height="18" primary-color="#1f2a3c" secondary-color="#2c3b55">
                <rect x="0" y="5" rx="2" ry="2" :width="(150 + Math.random() * 20)" height="10" />
            </ContentLoader>
          </div>
        </div>
      </template>
      <template v-else-if="runs.length == 0">
        <div class="run-card">
          <div class="run-info">
            <div class="headline">
                No runs to show yet!
            </div>
          </div>
        </div>
      </template>
      <template v-else-if="filteredEntries">
        <div v-if="filteredEntries.length == 0" class="run-card">
          <div class="run-info">
            <div class="headline">
                No runs match the specified filter(s)
            </div>
          </div>
        </div>
        <div class="run-card" v-for="entry in filteredEntries" :key="entry.RunId">
            <div class="run-info">
                <div class="headline">
                    <div class="ranks">
                      <span v-if="entry.Rank <= 3" class="trophy" v-bind:class="'rank-' + entry.Rank"></span>
                      <span v-if="entry.Rank > 3" class="rank">{{ entry.Rank }}</span>
                    </div>
                    <div>
                        <div  v-for="(p,j) in entry.Participants" :key="p.Username">
                            <router-link :to="'/profiles/' + p.Username">{{p.Username}}</router-link><span v-if="j != (entry.Participants.length - 1)">, </span>
                        </div>
                        <div>{{ entry.Points }} <span class="subtitle is-6">points</span></div>
                    </div>
                </div>
                <div class="time-info">
                    <a v-if="entry.Participants[0].EvidenceLink" :href="entry.Participants[0].EvidenceLink" target="_blank" rel="noopener noreferrer" class="timecode">{{ $filters.durationDisplay(entry.Duration) }}</a>
                    <span v-else class="timecode">{{$filters.durationDisplay(entry.Duration)}}</span>
                    <template v-if="entry.Participants.length > 1">
                      <template v-for="partner in entry.Participants.slice(1)" :key="partner.Username">
                        <a v-if="partner.EvidenceLink" :href="partner.EvidenceLink" :title="partner.Username" target="_blank" rel="noopener noreferrer" class="partner-evidence"><i class="fas fa-video"></i></a>
                      </template>
                    </template>
                    <div class="timestamp subtitle is-6">
                      {{formatDate(entry.OccurredAt)}}
                  </div>
                </div>
            </div>
            <div class="run-info">
                <div>
                    <template v-for="(filterValue, filter) in filters" :key="filter">
                        <span class="filter-value" v-if="entry.FilterValues[filter]">{{filter}}: {{entry.FilterValues[filter]}}</span>
                    </template>
                </div>
            </div>
        </div>
      </template>
      
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from "vue-facing-decorator";
import TimeService from "@/services/TimeService";
import { LeaderboardEntry, Run } from "@/api";
import { ContentLoader } from "vue-content-loader";

@Component({
  components: {
    ContentLoader
  },
})
export default class RunBoard extends Vue {
  filteredEntries: LeaderboardEntry[] = new Array();

  @Prop()
  runs: LeaderboardEntry[] = new Array();

  @Prop()
  filters: {[name: string]: string | null} = {};

  hasPlatformFilter = true;
  extraFilters: Record<string, string | null> = {};

  getHostname(url: string) {
    var rawHostname = new URL(url).hostname;

    return rawHostname.split(".").slice(-2).join(".");
  }

  mounted() {
    this.updateFilters();
  }

  @Watch("runs")
  public watchRuns() {
    this.refresh();
  }

  @Watch("filters", { deep: true})
  public updateFilters() {
    this.refresh();
    if(this.filters) {
      // Special casing Platform filter for table layout
      this.hasPlatformFilter = this.filters.hasOwnProperty("Platform");
      this.extraFilters = Object.assign({}, this.filters);
      delete this.extraFilters["Platform"];
    }
  }

  async refresh() {
    this.applyFilters();
  }

  applyFilters() {
    if(this.runs == null) return;

    let entries = this.runs;

    for(let filter of Object.keys(this.filters)) {
      if(this.filters[filter] != null) {
        entries = entries.filter(f => f.FilterValues == null || f.FilterValues[filter] == this.filters[filter]);
      }
    }

    this.filteredEntries = entries;
  }

  formatDate(date: string): string {
    return TimeService.iso8601ToShortFormat(date);
  }
}
</script>

<style scoped lang="scss">
@use "@/assets/haloruns_vars.scss" as *;

.partner-evidence {
  margin-left: 5px;
  border: 1px solid $primary;
  border-radius: 50%;
  padding: 4px;
  font-size: 10px;
}

.leaderboard-table {
  @include hr-tile-full-bleed;

  
  .ranks {
    text-align: left;
    padding-left: 25px;

    .rank {
      font-size: 1.0rem;
      font-family: "Oswald", sans-serif;
    }

    .trophy {
      display: block;
    }
  }

  .links {
    text-align: right;
    padding-right: 25px;
  }

  .run-info {
    border-bottom: none;

    td {
      padding-bottom: 0;
    }
  }

  .run-filters {
    td {
      padding-top: 0;
    }
  }
}

.filter-value {
  background: #666;
  font-size: 0.8rem;
  border-radius: 1.05rem;
  padding: 0.1rem 0.4rem 0.15rem 0.4rem;
  white-space: nowrap;
  margin-right: 0.4rem;
}

.run-cards {
    @include hr-tile-full-bleed;

    border-top: 2px solid $border-color;
    border-bottom: 2px solid $border-color;

    .run-card {
        padding-left: 25px;
        padding-right: 25px;
        padding-top: 7px;
        padding-bottom: 7px;
        
        &:not(:last-child) {
            border-bottom: 1px solid $border-color;
        }

        .ranks {
          text-align: left;
          .trophy {
            margin-top: 4px;
            margin-right: 15px;
          }
        }

        .run-info {
            display: flex;
            justify-content: space-between;

            .headline {
                display: flex;
                margin-bottom: 0.35rem;
            }
        }

        .time-info {
          text-align: right;
        }

        .timestamp {
            text-align: end;
            white-space: nowrap;
        }
    }
}
</style>
